import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useClientData } from '@/providers/ClientData.provider';
import { Select, TextField, SelectOption, toBool, sanitizeToNumeric, Flag, withStyles, FieldData } from 'vopay';
import styles from './TransactionCreationFields.module.scss';
const classes = withStyles(styles);

import { PaymentFieldDefault } from './types';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

export const useAmountFields = (
  availableBalance: string,
  AccountType: string,
  formType: 'Pay' | 'Collect',
): FieldData<PaymentFieldDefault>[] => {
  const {
    response: { USDEFTSendEnabled, Country, USDEFTCollectEnabled },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });

  const currencyOptions: SelectOption<BasicOptions>[] = [
    { label: 'CAD', value: 'CAD', isVisible: Country === 'CA' },
    {
      label: 'USD',
      value: 'USD',
      isVisible:
        Country !== 'CA' ||
        (Country === 'CA' && toBool(USDEFTSendEnabled) && formType == 'Pay') ||
        (Country === 'CA' && toBool(USDEFTCollectEnabled) && formType == 'Collect'),
    },
  ];

  return [
    {
      label: () => t('Fields.amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => {
        const cleanedValue = sanitizeToNumeric(values.Amount || '0.00');
        return Number(cleanedValue);
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            {...fieldData}
            variant="outlineShadow"
            preset="currency"
            placeholder="0.00"
            errorFitContent={true}
          />
        );
      },
      validate: () => {
        if (formType === 'Pay') {
          return Yup.string()
            .test('amount-validation', t('Fields.amount.minimumValidation'), (value) => {
              const cleanedValue = (value || '0.00').replace(/,/g, '');
              const amountValidation = Number(cleanedValue) <= 0.01;
              return !amountValidation;
            })
            .test(
              'amount-validation',
              t('Fields.amount.maximumValidation', {
                amount: String(availableBalance),
              }),
              (value) => {
                const cleanedValue = (value || '0.00').replace(/,/g, '');
                const availableFundsValidation =
                  Number(cleanedValue) > Number(availableBalance) && AccountType !== 'Technology';
                return !availableFundsValidation;
              },
            )
            .required();
        } else {
          return Yup.string()
            .required()
            .test('amount-validation', t('Fields.amount.minimumValidation'), (value) => {
              const cleanedValue = (value || '0.00').replace(/,/g, '');
              const amountValidation = Number(cleanedValue) <= 0.01;
              return !amountValidation;
            });
        }
      },
    },
    {
      label: () => <span>&nbsp;</span>,
      fieldName: 'Currency',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values) => values.Currency,
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('Fields.currency.placeholder')}
            options={currencyOptions.filter((option) => option.isVisible)}
            {...fieldData}
            onChange={(e) => {
              ctx.setValues({ ...ctx.values, PaymentMethod: '' });
              ctx.setTouched({ ...ctx.touched, PaymentMethod: false });
              fieldData.onChange(e);
            }}
            renderSelectedValue={(o, { disabled }) => (
              <div className={classes('currencyOption')}>
                <Flag className={classes(disabled && 'iconDisabled')} flag={o?.value || ''} width="24px" />{' '}
                {o?.label}{' '}
              </div>
            )}
            renderOption={(o, { Option }) => (
              <Option style={{ justifyContent: 'flex-start' }}>
                <div className={classes('currencyOption')}>
                  <Flag flag={o.value} width="24px" /> {o.label}
                </div>
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.mixed().required(),
    },
  ];
};
